<template>
    <v-container :fluid="true" class="max-height">
        <v-row>
            <v-col class="px-5 d-flex justify-space-between" cols="12">
                <h1 class="text-left">Alert Notification Recipients</h1>
                <v-btn :disabled="!company" color="black"
                       prepend-icon="mdi-plus" @click="()=>{selectedGroup= null; recipientsDialogSwitch=true }">Add
                </v-btn>
            </v-col>
        </v-row>
        <v-row>
            <v-col v-if="user.type === 'admin'" cols="12" md="4" sm="6">
                <company-select v-model="company" :items="companies" density="compact" label="Select Company"
                          @update:model-value="fetchGroups"/>
            </v-col>
        </v-row>
        <v-row>
            <v-col cols="12">
                <v-table>
                    <thead>
                    <tr>
                        <th-btn :sorted-column="sortedColumn??null" column="id"
                                sortable title="ID" @sort="sort"/>
                        <th-btn :sorted-column="sortedColumn??null" column="label"
                                sortable title="Label" @sort="sort"/>
                        <th-btn :sorted-column="sortedColumn??null" column="updated_at"
                                sortable title="Last Updated" @sort="sort"/>
                        <template v-if="user.type === 'admin'">
                            <th-btn :sorted-column="sortedColumn??null" column="company_name"
                                    sortable title="Company" @sort="sort"/>
                            <th-btn :sorted-column="sortedColumn??null" column="type"
                                    sortable title="Type" @sort="sort"/>
                        </template>
                        <th>Actions</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr v-for="group in groups" v-if="groups.length" :key="group.id"
                        class="text-left">
                        <td>{{ group.id }}</td>
                        <td>{{ group.label }}</td>
                        <td>{{ group.updated_at ? formatDateTime(group.updated_at) : '' }}</td>
                        <template v-if="user.type === 'admin'">
                            <td>{{ group.company_name }}</td>
                            <td>{{ group.type === 1 ? 'Default' : 'Non Default' }}</td>
                        </template>
                        <td>
                            <v-btn-group density="compact" divided variant="outlined">
                                <v-btn class="text-info" prepend-icon="mdi-email-outline" size="x-small"
                                       :loading="processing"
                                       @click="testEmailNotifications(group)">Test Email
                                </v-btn>
                                <v-btn class="text-purple" prepend-icon="mdi-cellphone-message" size="x-small"
                                       :loading="pushTesting"
                                       @click="testPushNotification(group)">Test Push
                                </v-btn>
                                <template v-if="group.type === 1">
                                    <v-btn class="text-success" prepend-icon="mdi-account-sync" size="x-small"
                                           :loading="syncing"
                                           @click="syncMembers(group)">Update Members
                                    </v-btn>
                                    <v-btn :disabled="recipientsDialogSwitch" class="text-warning"
                                           prepend-icon="mdi-account-multiple"
                                           size="x-small"
                                           @click="()=>{selectedGroup= group; recipientsDialogSwitch=true }">View
                                    </v-btn>
                                </template>
                                <template v-if="group.type > 1">
                                    <v-btn :disabled="recipientsDialogSwitch" class="text-warning"
                                           prepend-icon="mdi-account-multiple"
                                           size="x-small"
                                           @click="()=>{selectedGroup= group; recipientsDialogSwitch=true }">Edit
                                    </v-btn>
                                    <v-btn class="text-error" prepend-icon="mdi-delete" size="x-small"
                                           @click="deleteGroup(group)">Delete
                                    </v-btn>
                                </template>
                            </v-btn-group>
                        </td>
                    </tr>
                    <tr v-else>
                        <td v-if="loading" colspan="3">
                            <v-progress-circular color="primary" indeterminate/>
                        </td>
                        <td v-else colspan="7">No record found</td>
                    </tr>
                    </tbody>
                </v-table>
            </v-col>
        </v-row>
        <v-row v-if="totalPages > 1" justify="center">
            <v-col cols="8">
                <v-pagination
                    v-model="currentPage"
                    :length="totalPages"
                    class="my-4"
                    density="compact"
                    @update:model-value="fetchGroups"
                ></v-pagination>
            </v-col>
        </v-row>
    </v-container>

    <Recipients v-if="recipientsDialogSwitch" v-model="recipientsDialogSwitch" :company="company"
                :group="selectedGroup" :readOnly="selectedGroup?.type === 1" @addGroup="addGroup" @updateGroup="updateGroup"/>
</template>

<script setup>
import {onMounted, ref} from "vue";
import {send} from "@/composables/apiClient";
import {useAuthStore} from "@/stores/authStore";
import {useSnackbarStore} from "@/stores/snackBarStore";
import {formatDateTime} from '@/composables/useTimezone';
import ThBtn from "@/components/globals/th-btn.vue";
import Recipients from "@/components/dialogs/monitoring/notifications/Recipients.vue";
import CompanySelect from "@/components/globals/company-select.vue";

const {user} = useAuthStore();
const groups = ref([]);
const totalPages = ref(1);
const companies = ref([]);
const currentPage = ref(1);
const loading = ref(false);
const processing = ref(false);
const syncing = ref(false);
const pushTesting = ref(false);
const sortedType = ref('desc');
const sortedColumn = ref('updated_at');
const selectedGroup = ref(null);
const {showSnackbar} = useSnackbarStore();
const recipientsDialogSwitch = ref(false);
const company = ref((user.type !== 'admin') ? user.company.id : null);

onMounted(() => {
    if (user.type === 'admin') {
        fetchCompaniesList();
    }
    fetchGroups();
});
const fetchCompaniesList = () => {
    send('get', `companies/list`)
        .then((response) => {
            companies.value = response.data.data;
        })
        .catch((error) => {
            showSnackbar('Companies List could not be loaded', 'error')
        })
}
const sort = (sorting) => {
    sortedColumn.value = sorting.column;
    sortedType.value = sorting.order;
    fetchGroups();
}
const fetchGroups = () => {
    if (company.value) {
        loading.value = true;
        send('get', `monitoring/notification-groups`, {
            page: currentPage.value,
            company: company.value,
            sort: sortedColumn.value,
            order: sortedType.value
        })
            .then((response) => {
                loading.value = false;
                groups.value = response.data.data.data;
                totalPages.value = response.data.data.last_page;
                currentPage.value = response.data.data.current_page;
            })
            .catch((error) => {
                console.log(error)
                loading.value = false;
                showSnackbar('Notification groups could not be loaded', 'error')
            });
    }
}
const addGroup = (group) => {
    groups.value.unshift(group);
}
const updateGroup = (group) => {
    groups.value.map((sch, index) => {
        if (sch.id === group.id) {
            groups.value[index] = group;
        }
    });
}
const testEmailNotifications = (group) => {
    if (confirm(`Are you sure to test email notification for group ${group.label}?`)) {
        processing.value = true;
        send('post', `monitoring/notification-groups/${group.id}/test-email-notification`)
            .then((response) => {
                if (response.data.success) {
                    showSnackbar(response.data.message);
                }
            })
            .catch((error) => {
                if (error.response.data.message) {
                    showSnackbar(error.response.data.message, 'error')
                } else {
                    showSnackbar(`Something went wrong!`, 'error')
                }
            }).finally(() => processing.value = false);
    }
}
const testPushNotification = (group) => {
    if (confirm(`Send a test push notification to all members of "${group.label}"?`)) {
        pushTesting.value = true;
        send('post', `push-notifications/test`, {
            title: `Test from ${group.label}`,
            body: 'This is a test push notification from SimpliGPS Fleet',
            data: { source: 'notification_group_test', group_id: String(group.id) },
        })
            .then((response) => {
                if (response.data.success) {
                    const data = response.data.data || {};
                    showSnackbar(`Push sent! (${data.sent || 0} delivered, ${data.failed || 0} failed)`);
                }
            })
            .catch((error) => {
                showSnackbar(error.response?.data?.message || 'Failed to send test push', 'error');
            }).finally(() => pushTesting.value = false);
    }
}
const syncMembers = (group) => {
    if (confirm(`This will add all Company Admins and Managers to the notification group "${group.label}". Continue?`)) {
        syncing.value = true;
        send('post', `monitoring/notification-groups/${group.id}/sync-members`)
            .then((response) => {
                if (response.data.success) {
                    showSnackbar(response.data.message);
                }
            })
            .catch((error) => {
                if (error.response?.data?.message) {
                    showSnackbar(error.response.data.message, 'error')
                } else {
                    showSnackbar(`Something went wrong!`, 'error')
                }
            }).finally(() => syncing.value = false);
    }
}
const deleteGroup = (group) => {
    if (confirm(`Are you sure to delete selected group?`)) {
        send('delete', `monitoring/notification-groups/${group.id}`)
            .then((response) => {
                if (response.data.success) {
                    groups.value.map((item, index) => {
                        if (item.id === group.id) {
                            groups.value.splice(index, 1);
                        }
                    });
                    showSnackbar(response.data.message);
                }
            })
            .catch((error) => {
                if (error.response.data.message) {
                    showSnackbar(error.response.data.message, 'error')
                } else {
                    showSnackbar(`"${group.label}" group could not be deleted`, 'error')
                }
            })
    }
}
</script>
